import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { persistStorage } from '@/lib/impersonation-session';
import { useCanvasStateManager } from './canvasStateManager';

/**
 * Direct Sale runs its own step sequence rather than reusing the Seller
 * Creator's `design | products | publish`: the order differs, the actor
 * differs, and the flow ends in a purchase instead of a publish. Bending the
 * seller's steps to fit would couple two flows that need to move separately.
 */
export type DirectSaleStep = 'product' | 'design' | 'checkout';

export const DIRECT_SALE_STEPS: DirectSaleStep[] = [
  'product',
  'design',
  'checkout',
];

/** One position the customer has put a design on. */
export interface DecoratedPosition {
  positionId: string;
  widthMm: number | null;
  heightMm: number | null;
}

interface DirectSaleState {
  currentStep: DirectSaleStep;

  productId: string | null;
  colorId: string | null;
  /**
   * The size of a single piece (desktop checkout), or the first size with a
   * quantity on the phone. Kept in step with `quantities`, so the rest of the
   * app can keep reading one size.
   */
  sizeId: string | null;
  /** Pieces per size, the phone's "size and quantity" sheet. */
  quantities: Record<string, number>;
  decoratedPositions: DecoratedPosition[];
  /**
   * The decoration method picked for the whole product, or null for each
   * position's default. Not validated here: the readers drop a pick the
   * decorated positions no longer offer (see effectiveMethodId).
   */
  decorationMethodId: string | null;

  /** Which shop the persisted work belongs to — resume never crosses shops. */
  shop: string | null;
  /** Last meaningful change, for the resume freshness window. */
  savedAt: number | null;

  selectProduct: (productId: string) => void;
  /**
   * Picks a colour. With `sizeIds` (the sizes that colour carries), the
   * pieces of every other size are dropped and `sizeId` moves to the first
   * remaining one: a size the colour lacks cannot be quoted.
   */
  setColor: (colorId: string | null, sizeIds?: readonly string[]) => void;
  setSize: (sizeId: string | null) => void;
  setQuantity: (sizeId: string, quantity: number) => void;
  setDecoratedPositions: (positions: DecoratedPosition[]) => void;
  setDecorationMethod: (decorationMethodId: string | null) => void;
  setShop: (shop: string | null) => void;

  goToStep: (step: DirectSaleStep) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  reset: () => void;
}

const initialState = {
  currentStep: 'product' as DirectSaleStep,
  productId: null,
  colorId: null,
  sizeId: null,
  quantities: {} as Record<string, number>,
  decoratedPositions: [] as DecoratedPosition[],
  decorationMethodId: null as string | null,
  shop: null as string | null,
  savedAt: null as number | null,
};

/** How long an abandoned session stays resumable. */
export const RESUME_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Whether persisted work is worth offering to resume: same shop, a product
 * was chosen, and the last change is inside the freshness window. Pure so
 * the decision is testable without mounting the page.
 */
export function hasResumableSession(
  state: Pick<DirectSaleState, 'productId' | 'shop' | 'savedAt'>,
  shop: string | null,
  now: number = Date.now()
): boolean {
  return (
    state.productId !== null &&
    state.shop !== null &&
    state.shop === shop &&
    state.savedAt !== null &&
    now - state.savedAt < RESUME_MAX_AGE_MS
  );
}

/**
 * Whether a step has everything it needs from the steps before it.
 *
 * Reachability is derived from the data the customer has actually entered
 * rather than from separate "completed" flags — flags drift out of sync the
 * moment someone jumps back and clears a selection.
 */
export function isStepReachable(
  step: DirectSaleStep,
  state: Pick<DirectSaleState, 'productId' | 'decoratedPositions'>
): boolean {
  switch (step) {
    case 'product':
      return true;
    case 'design':
      return state.productId !== null;
    case 'checkout':
      return state.productId !== null && state.decoratedPositions.length > 0;
  }
}

export const useDirectSaleStore = create<DirectSaleState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        selectProduct: (productId) => {
          // Switching products invalidates the design and the variant picked
          // for the previous one — carrying them over would show a checkout
          // summary that never matches what the customer configured.
          const isSwitch =
            get().productId !== null && get().productId !== productId;

          // masterPositions is keyed by position SLUG and zustand-persisted —
          // product A's 'front' design would otherwise mark product B's
          // 'front' as decorated and show A's previews in B's checkout.
          // Cross-store call wrapped so a canvas failure never blocks
          // selection (cf. docs/solutions/ui-bugs/).
          if (isSwitch) {
            try {
              const canvasManager = useCanvasStateManager.getState();
              canvasManager.clearAllPositions();
              // The previous product's switch anchor must not carry its
              // design onto the next one (a Standard anchor would be
              // maximised onto a KLEIN mug); Direct Sale designs each
              // product from scratch (R21).
              canvasManager.clearSwitchAnchors();
            } catch {
              // Canvas manager not initialized yet; nothing to clear.
            }
          }

          set(
            {
              productId,
              ...(isSwitch
                ? {
                    colorId: null,
                    sizeId: null,
                    quantities: {},
                    decoratedPositions: [],
                    decorationMethodId: null,
                  }
                : {}),
              currentStep: 'design',
              savedAt: Date.now(),
            },
            false,
            'directSale/selectProduct'
          );
        },

        setColor: (colorId, sizeIds) => {
          if (!sizeIds) {
            set({ colorId, savedAt: Date.now() }, false, 'directSale/setColor');
            return;
          }
          const current = get().quantities;
          const quantities: Record<string, number> = {};
          // In the colour's size order, so the first remaining is stable.
          for (const id of sizeIds) {
            if (current[id]) quantities[id] = current[id];
          }
          set(
            {
              colorId,
              quantities,
              sizeId: Object.keys(quantities)[0] ?? null,
              savedAt: Date.now(),
            },
            false,
            'directSale/setColor'
          );
        },
        setSize: (sizeId) =>
          set(
            {
              sizeId,
              quantities: sizeId ? { [sizeId]: 1 } : {},
              savedAt: Date.now(),
            },
            false,
            'directSale/setSize'
          ),
        setQuantity: (sizeId, quantity) => {
          const quantities = { ...get().quantities };
          if (quantity > 0) quantities[sizeId] = quantity;
          else delete quantities[sizeId];
          set(
            {
              quantities,
              sizeId: Object.keys(quantities)[0] ?? null,
              savedAt: Date.now(),
            },
            false,
            'directSale/setQuantity'
          );
        },
        setDecorationMethod: (decorationMethodId) =>
          set(
            { decorationMethodId, savedAt: Date.now() },
            false,
            'directSale/setDecorationMethod'
          ),
        setShop: (shop) => set({ shop }, false, 'directSale/setShop'),

        setDecoratedPositions: (decoratedPositions) => {
          const state = get();
          // Clearing the last decoration makes checkout unreachable; drop back
          // rather than stranding the customer on a step with nothing to buy.
          const fallback =
            state.currentStep === 'checkout' && decoratedPositions.length === 0
              ? { currentStep: 'design' as DirectSaleStep }
              : {};

          set(
            { decoratedPositions, ...fallback, savedAt: Date.now() },
            false,
            'directSale/setDecoratedPositions'
          );
        },

        goToStep: (step) => {
          if (!isStepReachable(step, get())) return;
          set({ currentStep: step }, false, 'directSale/goToStep');
        },

        goToNextStep: () => {
          const index = DIRECT_SALE_STEPS.indexOf(get().currentStep);
          const next = DIRECT_SALE_STEPS[index + 1];
          if (next) get().goToStep(next);
        },

        goToPreviousStep: () => {
          const index = DIRECT_SALE_STEPS.indexOf(get().currentStep);
          const previous = DIRECT_SALE_STEPS[index - 1];
          if (previous) get().goToStep(previous);
        },

        reset: () => set(initialState, false, 'directSale/reset'),
      }),
      {
        name: 'direct-sale-storage',
        // sessionStorage in an impersonation tab, localStorage otherwise.
        storage: persistStorage(),
        // The step is derived UX state: a resumed session re-enters at the
        // design step explicitly, never wherever the customer happened to
        // close the tab.
        // Work saved before quantities existed carried one size: that is
        // one piece of it.
        version: 1,
        migrate: (persisted) => {
          const state = persisted as Partial<DirectSaleState>;
          return {
            ...state,
            quantities:
              state.quantities ?? (state.sizeId ? { [state.sizeId]: 1 } : {}),
          };
        },
        partialize: (state) => ({
          productId: state.productId,
          colorId: state.colorId,
          sizeId: state.sizeId,
          quantities: state.quantities,
          decoratedPositions: state.decoratedPositions,
          decorationMethodId: state.decorationMethodId,
          shop: state.shop,
          savedAt: state.savedAt,
        }),
      }
    ),
    { name: 'DirectSaleStore' }
  )
);
